import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBranchFreeze } from "@/lib/useBranchFreeze";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Users, Camera, AlertCircle, Search, MoreHorizontal,
  Eye, Pencil, Ban, ToggleRight, Trash2, ImageIcon, KeyRound,
} from "lucide-react";
import StaffAccessDialog from "@/pages/owner/employees/StaffAccessDialog";
import ClearancesTab from "@/pages/owner/employees/ClearancesTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePagination } from "@/components/Pagination";

const EMPTY_FORM = {
  name: "",
  picture: "",
  birthday: "",
  phone: "",
  email: "",
  employment_status: "active",
  position: "",
  date_of_hire: "",
  school_id: "",
  branch_id: "",
  license_number: "",
  sss_no: "",
  philhealth_no: "",
  pagibig_no: "",
};

const STATUS_LABELS = {
  active: { label: "Active", chip: "bg-[#E8F5E9] text-[#2E7D32]", dot: "bg-[#2E7D32]" },
  inactive: { label: "Inactive", chip: "bg-brand-purpleLight text-brand-inkMuted", dot: "bg-brand-inkMuted" },
  terminated: { label: "Terminated", chip: "bg-[#FFEBEE] text-[#C62828]", dot: "bg-[#C62828]" },
};

export default function EmployeeListPage() {
  const { approvedSchools, allBranches, selectedSchool, selectedBranch } = useBranch();
  const { user } = useAuth();
  const { frozenBranch, lockedBranchId } = useBranchFreeze();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms.employees_create;
  const canEdit = isOwner || !!perms.employees_edit;
  const canDelete = isOwner || !!perms.employees_delete;
  // Staff access management (granting login + perms) is owner-only by design.
  const canManageStaffAccess = isOwner;
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // employee object when editing
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pictureBytes, setPictureBytes] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState(() => lockedBranchId || "all");
  const [accessTarget, setAccessTarget] = useState(null);
  const [viewingTab, setViewingTab] = useState("profile");

  // Deep-link support: dashboard widget can open this page with
  // ``?employee=<id>&tab=clearances`` to auto-open the view dialog on the
  // Clearances tab. Once consumed the params stay (so refresh re-opens) — a
  // ref prevents re-firing the same id over and over.
  const [searchParams] = useSearchParams();
  const deepLinkConsumedRef = useRef(null);

  const branchesForFormSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === form.school_id && b.status === "approved"),
    [allBranches, form.school_id]
  );
  const branchesForSelectedSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchool?.id && b.status === "approved"),
    [allBranches, selectedSchool?.id]
  );

  const load = useCallback(async () => {
    if (!selectedSchool) { setEmployees([]); return; }
    setLoading(true);
    try {
      // Fetch employees across all approved branches of the active school
      const results = await Promise.all(
        branchesForSelectedSchool.map((b) =>
          api.get("/employees", { params: { branch_id: b.id } }).then((r) => r.data || [])
        )
      );
      setEmployees(results.flat());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedSchool, branchesForSelectedSchool]);

  useEffect(() => { load(); }, [load]);

  // Honour ``?employee=<id>&tab=clearances`` once the employee list has loaded.
  useEffect(() => {
    const empId = searchParams.get("employee");
    if (!empId || employees.length === 0) return;
    if (deepLinkConsumedRef.current === empId) return;
    const target = employees.find((e) => e.id === empId);
    if (target) {
      deepLinkConsumedRef.current = empId;
      setViewing(target);
      setViewingTab(searchParams.get("tab") === "clearances" ? "clearances" : "profile");
    }
  }, [employees, searchParams]);

  // Frozen branch users always operate within their assigned branch.
  const effBranchFilter = frozenBranch && lockedBranchId ? lockedBranchId : branchFilter;

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      school_id: selectedSchool?.id || "",
      branch_id: selectedBranch?.id || "",
    });
    setPictureBytes(0);
    setOpenForm(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      ...EMPTY_FORM,
      ...emp,
    });
    setPictureBytes(emp.picture ? Math.floor((emp.picture.length * 3) / 4) : 0);
    setOpenForm(true);
  };

  const onPickPicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCompressing(true);
    try {
      const dataUrl = await compressImage(file, { maxKB: 400, maxDim: 1280 });
      setForm((f) => ({ ...f, picture: dataUrl }));
      const b64 = dataUrl.split(",")[1] || "";
      const bytes = Math.floor((b64.length * 3) / 4);
      setPictureBytes(bytes);
      toast.success(`Picture compressed to ${(bytes / 1024).toFixed(0)} KB`);
    } catch (err) {
      toast.error(err.message || "Could not process image");
    } finally {
      setCompressing(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.school_id || !form.branch_id) {
      toast.error("Please pick an assigned school and branch");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/employees/${editing.id}`, form);
        setEmployees((prev) => prev.map((e) => (e.id === editing.id ? data : e)));
        toast.success(`${form.name} updated`);
      } else {
        const { data } = await api.post("/employees", form);
        setEmployees((prev) => [data, ...prev]);
        toast.success(`${form.name} added`);
      }
      setOpenForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      setPictureBytes(0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (emp, nextStatus) => {
    try {
      const { data } = await api.patch(`/employees/${emp.id}`, { employment_status: nextStatus });
      setEmployees((prev) => prev.map((e) => (e.id === emp.id ? data : e)));
      toast.success(`${emp.name}: ${STATUS_LABELS[nextStatus].label}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      setEmployees((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      toast.success(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const branchNameById = useMemo(() => {
    const m = {};
    allBranches.forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [allBranches]);

  const ql = q.toLowerCase();
  const filtered = employees.filter((e) => {
    if (statusFilter !== "all" && (e.employment_status || "active") !== statusFilter) return false;
    if (effBranchFilter !== "all" && e.branch_id !== effBranchFilter) return false;
    if (!ql) return true;
    return (
      e.name.toLowerCase().includes(ql) ||
      (e.position || "").toLowerCase().includes(ql) ||
      (e.email || "").toLowerCase().includes(ql) ||
      (e.phone || "").toLowerCase().includes(ql)
    );
  });

  // Pagination — resets to page 1 when filters change.
  const { pageItems: pagedEmployees, controls: paginationControls } = usePagination(
    filtered,
    { pageSize: 25, label: "employees", resetKey: `${q}|${statusFilter}|${effBranchFilter}|${selectedSchool?.id || ""}` },
  );

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Employees</h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm">
            {selectedSchool
              ? <>Manage employees of <span className="font-semibold text-foreground">{selectedSchool.name}</span>.</>
              : "Pick a school first to manage employees."}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={openAdd}
            disabled={!selectedSchool}
            className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5"
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Employee
          </Button>
        )}
      </div>

      {!selectedSchool ? (
        <NoSchoolCard />
      ) : (
        <>
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email, phone, or position..."
                  className="rounded-xl h-10 pl-9 border-brand-purple/15 text-sm"
                  data-testid="employees-search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                data-testid="employees-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
              </select>
              <select
                value={effBranchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={frozenBranch}
                title={frozenBranch ? "Locked to your assigned branch" : undefined}
                className="h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple disabled:bg-brand-purpleLight/30 disabled:cursor-not-allowed disabled:opacity-80"
                data-testid="employees-branch-filter"
              >
                {!frozenBranch && <option value="all">All branches</option>}
                {branchesForSelectedSchool.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </section>

          {loading ? (
            <div className="text-brand-inkMuted font-heading">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-brand-purple/10 p-10 text-center" data-testid="no-employees">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
                <Users className="w-7 h-7 text-brand-purple" />
              </div>
              <p className="font-heading text-lg font-semibold text-foreground">
                {q || statusFilter !== "all" || effBranchFilter !== "all" ? "No employees match your filters" : "No employees yet"}
              </p>
              {!q && statusFilter === "all" && effBranchFilter === "all" && (
                <p className="text-brand-inkMuted text-sm">Click "Add Employee" to onboard your first one.</p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="employees-table">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[30%]" />
                  <col className="w-[20%]" />
                  <col className="w-[16%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[8%]" />
                </colgroup>
                <thead className="bg-[#F1F5FF] text-brand-inkMuted">
                  <tr>
                    <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider text-[11px]">EMPLOYEE</th>
                    <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">POSITION</th>
                    <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">BRANCH</th>
                    <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">CONTACT</th>
                    <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">STATUS</th>
                    <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider text-[11px]">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedEmployees.map((emp) => (
                    <EmployeeRow
                      key={emp.id}
                      emp={emp}
                      branchName={branchNameById[emp.branch_id]}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      canManageStaffAccess={canManageStaffAccess}
                      onView={() => setViewing(emp)}
                      onEdit={() => openEdit(emp)}
                      onTerminate={() => setTerminateTarget(emp)}
                      onToggleActive={() => setStatus(emp, emp.employment_status === "active" ? "inactive" : "active")}
                      onDelete={() => setDeleteTarget(emp)}
                      onManageAccess={() => setAccessTarget(emp)}
                    />
                  ))}
                </tbody>
              </table>
              {paginationControls}
            </div>
          )}
        </>
      )}

      {/* Form dialog (Add + Edit) */}
      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent
          className="rounded-3xl border-brand-purple/20 max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="employee-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Users className="w-5 h-5 text-brand-purple" />
              {editing ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
            <DialogDescription>
              Upload a profile picture (auto-compressed to ≤300 KB) and fill in the details below.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5" data-testid="employee-form">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-24 h-24 rounded-3xl bg-brand-purpleLight border border-brand-purple/20 overflow-hidden flex items-center justify-center shrink-0">
                {form.picture ? (
                  <img src={form.picture} alt="preview" className="w-full h-full object-cover" data-testid="employee-picture-preview" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-brand-purple" />
                )}
              </div>
              <div className="flex-1">
                <Label className="font-heading">Profile picture</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <label
                    htmlFor="employee-picture-input"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-purpleLight px-4 py-2 text-sm font-semibold text-brand-purpleHover cursor-pointer hover:bg-brand-purple hover:text-white transition-colors"
                  >
                    <Camera className="w-4 h-4" /> {form.picture ? "Replace" : "Upload"}
                  </label>
                  <input id="employee-picture-input" type="file" accept="image/*" onChange={onPickPicture} className="hidden" data-testid="employee-picture-input" />
                  {compressing && <span className="text-xs text-brand-inkMuted">Compressing...</span>}
                  {!compressing && pictureBytes > 0 && (
                    <span className="text-xs text-brand-inkMuted">{(pictureBytes / 1024).toFixed(0)} KB</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="Maria Dela Cruz" data-testid="employee-name-input" />
              </Field>
              <Field label="Position" required>
                <Input required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="Mathematics Teacher" data-testid="employee-position-input" />
              </Field>
              <Field label="Birthday">
                <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="employee-birthday-input" />
              </Field>
              <Field label="Date of hire" required>
                <Input type="date" required value={form.date_of_hire} onChange={(e) => setForm({ ...form, date_of_hire: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="employee-date-of-hire-input" />
              </Field>
              <Field label="Phone number">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="+63 9XX XXX XXXX" data-testid="employee-phone-input" />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="maria@school.com" data-testid="employee-email-input" />
              </Field>
              <Field label="Employment status" required>
                <select required value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple" data-testid="employee-status-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </select>
              </Field>
              <Field label="License number">
                <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="employee-license-input" />
              </Field>
              <Field label="Assigned school" required>
                <select required value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value, branch_id: "" })} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple" data-testid="employee-school-select">
                  <option value="">Select school...</option>
                  {approvedSchools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Assigned branch" required>
                <select required value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} disabled={!form.school_id} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple" data-testid="employee-branch-select">
                  <option value="">Select branch...</option>
                  {branchesForFormSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="SSS No."><Input value={form.sss_no} onChange={(e) => setForm({ ...form, sss_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="00-0000000-0" data-testid="employee-sss-input" /></Field>
              <Field label="PhilHealth No."><Input value={form.philhealth_no} onChange={(e) => setForm({ ...form, philhealth_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="00-000000000-0" data-testid="employee-philhealth-input" /></Field>
              <Field label="Pag-IBIG No." className="sm:col-span-2"><Input value={form.pagibig_no} onChange={(e) => setForm({ ...form, pagibig_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="0000-0000-0000" data-testid="employee-pagibig-input" /></Field>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy || compressing} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="employee-submit-btn">
                {busy ? "Saving..." : editing ? "Save changes" : "Save employee"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setViewingTab("profile"); } }}>
        <DialogContent className="rounded-3xl border-0 max-w-2xl p-0 overflow-hidden bg-white" data-testid="employee-view-dialog">
          {viewing && (
            <>
              {/* Gradient header */}
              <div className="relative h-28 bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#60A5FA]">
                <div className="absolute -bottom-12 left-6">
                  <div className="w-24 h-24 rounded-3xl ring-4 ring-white shadow-lg bg-brand-purpleLight overflow-hidden flex items-center justify-center">
                    {viewing.picture ? (
                      <img src={viewing.picture} alt={viewing.name} className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-10 h-10 text-brand-purple" />
                    )}
                  </div>
                </div>
                <span className={`absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold backdrop-blur ${
                  (viewing.employment_status || "active") === "active"
                    ? "bg-white/95 text-[#2E7D32]"
                    : (viewing.employment_status === "terminated"
                        ? "bg-white/95 text-[#C62828]"
                        : "bg-white/95 text-brand-inkMuted")
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    (viewing.employment_status || "active") === "active"
                      ? "bg-[#2E7D32]"
                      : (viewing.employment_status === "terminated" ? "bg-[#C62828]" : "bg-brand-inkMuted")
                  }`} />
                  {STATUS_LABELS[viewing.employment_status || "active"]?.label}
                </span>
              </div>

              {/* Body */}
              <div className="pt-16 pb-6 px-6">
                <DialogHeader className="text-left space-y-1 mb-5">
                  <DialogTitle className="font-heading text-2xl sm:text-3xl uppercase tracking-tight" data-testid="view-employee-name">
                    {viewing.name}
                  </DialogTitle>
                  <DialogDescription className="uppercase tracking-[0.18em] text-xs text-brand-inkMuted">
                    {viewing.position || "—"}
                  </DialogDescription>
                </DialogHeader>

                <Tabs value={viewingTab} onValueChange={setViewingTab} className="w-full">
                  <TabsList className="grid grid-cols-2 w-full max-w-sm mb-4 bg-brand-purpleLight/50 rounded-2xl p-1">
                    <TabsTrigger
                      value="profile"
                      className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                      data-testid="view-tab-profile"
                    >
                      Profile
                    </TabsTrigger>
                    <TabsTrigger
                      value="clearances"
                      className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                      data-testid="view-tab-clearances"
                    >
                      Documents
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="profile" className="mt-0">
                    {/* Contact group */}
                    <ViewGroup title="Contact">
                      <ViewItem label="Branch" value={branchNameById[viewing.branch_id]} />
                      <ViewItem label="Email" value={viewing.email} />
                      <ViewItem label="Phone" value={viewing.phone} />
                    </ViewGroup>

                    {/* Employment group */}
                    <ViewGroup title="Employment">
                      <ViewItem label="Date of Hire" value={viewing.date_of_hire} />
                      <ViewItem label="Birthday" value={viewing.birthday} />
                      <ViewItem label="License No." value={viewing.license_number} />
                    </ViewGroup>

                    {/* Government IDs */}
                    <ViewGroup title="Government IDs" lastGroup>
                      <ViewItem label="SSS" value={viewing.sss_no} mono />
                      <ViewItem label="PhilHealth" value={viewing.philhealth_no} mono />
                      <ViewItem label="Pag-IBIG" value={viewing.pagibig_no} mono />
                    </ViewGroup>
                  </TabsContent>

                  <TabsContent value="clearances" className="mt-0">
                    <ClearancesTab employee={viewing} />
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-6">
                  <Button
                    onClick={() => setViewing(null)}
                    className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white px-6 h-10"
                    data-testid="view-close-btn"
                  >
                    Close
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Terminate confirmation */}
      <AlertDialog open={!!terminateTarget} onOpenChange={(o) => !o && setTerminateTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="terminate-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Terminate {terminateTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The employee will be marked as <span className="font-semibold text-foreground">Terminated</span> and excluded from new pay slips. You can revert this by editing the employee record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setStatus(terminateTarget, "terminated"); setTerminateTarget(null); }}
              className="rounded-full bg-[#E65100] hover:bg-[#BF360C] text-white"
              data-testid="confirm-terminate-employee"
            >
              Terminate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the employee record. Attendance and payslip history (when added) won't be retained.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-delete-employee">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff Access dialog */}
      <StaffAccessDialog
        open={!!accessTarget}
        onOpenChange={(o) => { if (!o) setAccessTarget(null); }}
        employee={accessTarget}
        branches={allBranches}
      />
    </>
  );
}

function EmployeeRow({ emp, branchName, canEdit, canDelete, canManageStaffAccess, onView, onEdit, onTerminate, onToggleActive, onDelete, onManageAccess }) {
  const status = STATUS_LABELS[emp.employment_status || "active"] || STATUS_LABELS.active;
  const isActive = emp.employment_status === "active";
  return (
    <tr className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`employee-row-${emp.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full bg-brand-purpleLight overflow-hidden flex items-center justify-center shrink-0">
            {emp.picture ? (
              <img src={emp.picture} alt={emp.name} className="w-full h-full object-cover" />
            ) : (
              <Users className="w-5 h-5 text-brand-purple" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-foreground text-sm uppercase truncate" data-testid={`employee-name-${emp.id}`}>{emp.name}</p>
            <p className="text-xs text-brand-inkMuted truncate">{emp.email || "—"}</p>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <p className="uppercase text-xs sm:text-sm text-foreground/80 truncate">{emp.position || "—"}</p>
      </td>
      <td className="px-3 py-3">
        <p className="text-xs sm:text-sm text-foreground/80 truncate">{branchName || "—"}</p>
      </td>
      <td className="px-3 py-3">
        <p className="text-xs sm:text-sm text-foreground/80">{emp.phone || "—"}</p>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${status.chip}`} data-testid={`employee-status-${emp.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </td>
      <td className="px-3 py-3 text-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-inkMuted hover:bg-brand-purpleLight hover:text-foreground" data-testid={`actions-trigger-${emp.id}`}>
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl w-44 p-1.5">
            <DropdownMenuItem onClick={onView} className="rounded-xl gap-2 cursor-pointer" data-testid={`view-employee-${emp.id}`}>
              <Eye className="w-4 h-4" /> View
            </DropdownMenuItem>
            {canEdit && (
              <DropdownMenuItem onClick={onEdit} className="rounded-xl gap-2 cursor-pointer" data-testid={`edit-employee-${emp.id}`}>
                <Pencil className="w-4 h-4" /> Edit
              </DropdownMenuItem>
            )}
            {canManageStaffAccess && (
              <DropdownMenuItem onClick={onManageAccess} className="rounded-xl gap-2 cursor-pointer" data-testid={`manage-access-${emp.id}`}>
                <KeyRound className="w-4 h-4" /> Manage Access
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem onClick={onTerminate} className="rounded-xl gap-2 cursor-pointer text-[#E65100] focus:text-[#E65100]" data-testid={`terminate-employee-${emp.id}`}>
                <Ban className="w-4 h-4" /> Terminate
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem onClick={onToggleActive} className="rounded-xl gap-2 cursor-pointer" data-testid={`toggle-active-employee-${emp.id}`}>
                <ToggleRight className="w-4 h-4" /> {isActive ? "Deactivate" : "Activate"}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem onClick={onDelete} className="rounded-xl gap-2 cursor-pointer text-[#C62828] focus:text-[#C62828]" data-testid={`delete-employee-${emp.id}`}>
                <Trash2 className="w-4 h-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function ViewGroup({ title, children, lastGroup }) {
  // Filter out children whose value is empty so we don't render empty groups
  const items = React.Children.toArray(children).filter((c) => c?.props?.value);
  if (items.length === 0) return null;
  return (
    <div className={lastGroup ? "" : "border-b border-brand-purple/10 pb-4 mb-4"}>
      <p className="font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-purple mb-3">{title}</p>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {items}
      </dl>
    </div>
  );
}

function ViewItem({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-[0.15em] text-brand-inkMuted">{label}</dt>
      <dd className={`text-sm text-foreground mt-0.5 break-words ${mono ? "font-mono tracking-wide" : ""}`}>{value}</dd>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-heading">
        {label} {required && <span className="text-[#C62828]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NoSchoolCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-school-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">
        Employees are scoped per branch within a school. Use the school/branch dropdown in the header.
      </p>
    </div>
  );
}
